import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useDataSource } from '@afosto/app-components-react/DataSource';
import { usePickContext } from '../PickProvider';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import ProcessHandlingListContext from '../../../../components/ProcessHandlingListContext';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import ItemQuantityDialog from '../../../../components/ItemQuantityDialog';
import MissingItem from './MissingItem';
import MissingItemParcels from './MissingItemParcels';
import MissingItemSkeleton from './MissingItemSkeleton';
import PickMaxQuantityDialog from '../PickMaxQuantityDialog';
import DataList from '@afosto/app-components-react/DataList';
import Dialog from '@afosto/app-components-react/Dialog';
import DialogToolbar from '@afosto/app-components-react/DialogToolbar';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import fetchMultipleProducts from '@afosto/utils-react/fetchMultipleProducts';
import { bulk as bulkApi } from '@afosto/fulfilment/api/Bulk';
import { search as searchApi } from '@afosto/fulfilment/api/Search';
import translations from './translations';
import styled from 'styled-components';

const DEFAULT_ITEM_STATUS_CONSTRAINTS = [
  {
    key: 'is_handled',
    operator: 'in',
    value: false,
  },
  {
    key: 'is_finished',
    operator: 'in',
    value: false,
  },
  {
    key: 'is_missing',
    operator: 'in',
    value: false,
  },
];

const DataListContainerGrid = props => <Grid container spacing={1} {...props} />;

const DialogContainer = styled(Box)`
  && {
    background-color: ${props => props.theme.palette.grey[50]};
  }
`;

const PickMissingItemsDialog = ({ isOpen, onFinish, onSaveItem }) => {
  const { handlingList } = useContext(ProcessHandlingListContext);
  const { currentParcel } = usePickContext();
  const handlingListId = handlingList?.id;
  const intl = useIntl();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const [selectedItem, setSelectedItem] = useState(null);
  const [showMaxQuantityDialog, setShowMaxQuantityDialog] = useState(false);

  const fetchMissingItems = async dataSource =>
    dataSourceRequest(
      dataSource,
      async (data, requestOptions) => {
        if (!isOpen) {
          return Promise.reject();
        }

        return searchApi.searchHandlingListItems(
          {
            constraints: [
              {
                key: 'list_ids',
                operator: 'in',
                value: [handlingListId],
              },
              ...DEFAULT_ITEM_STATUS_CONSTRAINTS,
            ],
          },
          requestOptions,
        );
      },
      {
        transformData: async (data, source) => {
          const { pagination = {} } = source ?? {};
          const { itemsPerPage } = pagination;
          const [firstItem] = data ?? [];
          const skus = data.map(({ sku }) => sku);
          const products = await fetchMultipleProducts(
            skus,
            {},
            {
              headers: {
                'x-page-size': itemsPerPage,
              },
            },
          );
          const mappedProducts = products.reduce(
            (acc, product) => ({ ...acc, [product?.sku]: product }),
            {},
          );
          const response = await searchApi.listListItems({
            constraints: [
              {
                key: 'ids',
                operator: 'in',
                value: firstItem.ids ?? [],
              },
              {
                key: 'shipment_ids',
                operator: 'in',
                value: [firstItem.shipment_id],
              },
            ],
          });
          const [shipmentItem] = response?.data ?? [];
          const itemParcels = (shipmentItem?.parcels ?? []).reduce(
            (acc, parcel) => ({ ...acc, [parcel.id]: parcel }),
            {},
          );
          const boxedIds = (shipmentItem?.parcels ?? []).reduce(
            (acc, parcel) => [...acc, ...(parcel?.item_ids ?? [])],
            [],
          );

          return data.reduce((acc, item) => {
            const [firstId] = item?.ids ?? [];
            return [
              ...acc,
              {
                ...item,
                id: firstId,
                product: mappedProducts[item.sku],
                parcels: itemParcels,
                boxed_ids: boxedIds,
              },
            ];
          }, []);
        },
      },
    );

  const dataSource = useDataSource(fetchMissingItems, {
    modules: ['pagination'],
  });
  const dataSourceRef = useRef(dataSource);

  useEffect(() => {
    dataSourceRef.current = dataSource;
  });

  useEffect(() => {
    if (isOpen) {
      dataSourceRef.current.reloadDataSource();
    }
  }, [isOpen]);

  const handleSelectItem = item => {
    setSelectedItem(item);
  };

  const saveItem = async ({ requested, quantity, position }, item) => {
    if (onSaveItem && typeof onSaveItem === 'function') {
      return onSaveItem(item, { requested, quantity }, position);
    }

    return Promise.resolve();
  };

  const handleSaveItem = async (data, item) => {
    await saveItem(data, item);
    dataSource.reloadDataSource();
    setSelectedItem(null);
  };

  const handleBeforeSwitchParcel = async (data, item) => {
    const { ids: updatedItemIds = [] } = await saveItem(data, item);
    const ids = selectedItem.ids.filter(itemId => !updatedItemIds?.includes(itemId));

    if (ids.length === 0) {
      dataSource.reloadDataSource();
      setSelectedItem(null);
    } else {
      const itemParcels = selectedItem?.parcels ?? {};
      const currentItemParcel = itemParcels[currentParcel?.id];

      setSelectedItem({
        ...selectedItem,
        ids,
        handled_ids: [],
        quantity: ids?.length,
        position: data?.position ?? selectedItem?.position,
        boxed_ids: [...(selectedItem?.boxed_ids ?? []), ...updatedItemIds],
        parcels: {
          ...itemParcels,
          [currentParcel?.id]: {
            ...(currentItemParcel ?? {}),
            item_ids: [...(currentItemParcel?.item_ids ?? []), ...updatedItemIds],
          },
        },
      });
    }
  };

  const handleCloseItemDialog = () => {
    setSelectedItem(null);
  };

  const handleMaxQuantityReached = () => {
    setShowMaxQuantityDialog(true);
  };

  const handleCloseMaxQuantityDialog = () => {
    setShowMaxQuantityDialog(false);
  };

  const handleFinishMissingItems = async () => {
    try {
      await bulkApi.bulkUpdateItems({
        command: 'set_parameters',
        parameters: {
          is_missing: true,
        },
        constraints: [
          {
            key: 'list_ids',
            operator: 'in',
            value: [handlingListId],
          },
          ...DEFAULT_ITEM_STATUS_CONSTRAINTS,
        ],
      });

      if (onFinish && typeof onFinish === 'function') {
        onFinish();
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.missingItemsNotReported), {
        variant: 'error',
      });
    }
  };

  const renderItem = item => <MissingItem key={item.id} item={item} onClick={handleSelectItem} />;

  const renderSkeleton = key => <MissingItemSkeleton key={key} />;

  return (
    <>
      <Dialog open={isOpen} fullScreen={isMobile} fullWidth>
        <DialogToolbar title={intl.formatMessage(translations.title)} />
        <DialogContainer display="flex" flexDirection="column" flex={1}>
          <Box p={2} flex={1}>
            <DataList
              component={DataListContainerGrid}
              dataSource={dataSource}
              renderItem={renderItem}
              renderSkeletonItem={renderSkeleton}
            />
          </Box>
          <DialogActions>
            <Divider />
            <Button color="primary" variant="contained" onClick={handleFinishMissingItems}>
              <FormattedMessage {...translations.actions.finish} />
            </Button>
          </DialogActions>
        </DialogContainer>
      </Dialog>
      <ItemQuantityDialog
        title={intl.formatMessage(translations.itemQuantityDialog.title)}
        isOpen={!!selectedItem}
        item={selectedItem}
        locationId={handlingList.location_id}
        onBeforeSwitchParcel={handleBeforeSwitchParcel}
        onClose={handleCloseItemDialog}
        onMaxQuantityReached={handleMaxQuantityReached}
        onSave={handleSaveItem}
        PackingComponent={MissingItemParcels}
        positionRequired={false}
        quantityCounterProps={{
          requestedLabel: intl.formatMessage(translations.properties.quantity),
          quantityLabel: intl.formatMessage(translations.properties.picked),
        }}
        initPositionBasedOnItem
      />
      <PickMaxQuantityDialog
        isOpen={showMaxQuantityDialog}
        onClose={handleCloseMaxQuantityDialog}
      />
    </>
  );
};

PickMissingItemsDialog.propTypes = {
  isOpen: PropTypes.bool,
  onFinish: PropTypes.func,
  onSaveItem: PropTypes.func,
};

PickMissingItemsDialog.defaultProps = {
  isOpen: false,
  onFinish: undefined,
  onSaveItem: undefined,
};

export default PickMissingItemsDialog;
